import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';

const MIN_RESIZE_DELTA = 30;

interface ProportionalContainerComponentProps {
  ratio: number;
  children?: ReactNode;
}

function ProportionalContainerComponent(
  { ratio, children: renderChildren }: ProportionalContainerComponentProps,
  ref
) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, ratio });

  const updateDimensions = useCallback((newWidth, newRatio) => {
    if (newWidth > 0 && newRatio > 0) {
      const newHeight = newWidth / newRatio;
      setDimensions({ width: newWidth, height: newHeight, ratio: newRatio });
    }
  }, []);

  useEffect(() => {
    const { current: refElement } = ref;

    if (!refElement || ratio <= 0) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length) {
        const { width: newWidth } = entries[0].contentRect;

        if (
          Math.abs(dimensions.width - newWidth) > MIN_RESIZE_DELTA ||
          ratio !== dimensions.ratio
        ) {
          updateDimensions(newWidth, ratio);
        }
      }
    });

    resizeObserver.observe(refElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, ratio, dimensions.width, dimensions.ratio, updateDimensions]);

  if (!dimensions.width || !dimensions.height) {
    return null;
  }

  return renderChildren(dimensions.width, dimensions.height);
}

ProportionalContainerComponent.displayName = 'ProportionalContainerComponent';

export const ProportionalContainer = forwardRef(ProportionalContainerComponent);
